import { useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import { useMediaQuery } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { TabProps } from "components/NavSwitcher";
import { Slider } from "components/Slider/Slider";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import { OrderStatusList, ParcelStatusList } from "constants/status";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { OrderActionType, ParcelActionType } from "data/types";
import { parcelsAndOrdersReducer } from "domains/Parcel/data/fetchData";
import { formatReceiver } from "domains/Receiver/Components/NewReceiver";
import { dateContext } from "domains/Task/components/HomeSides/Recap/context/dateContext";
import { formatParcel } from "domains/Task/components/Job/JobRecap";
import { deleteOrder, UPDATE_ORDER } from "domains/Task/graphql/mutations";
import { ReceiverByClientID } from "domains/Task/graphql/queries";
import { AllOrderSubscription, AllParcelsSubscription } from "domains/Task/graphql/suscribtions";
import { Order, Parcel } from "generated/graphql";
import { GET_ORDERS_BY_CLIENT, GET_PARCELS_BY_CLIENT } from "graphql/Parcel/queries";
import { useCurrentClient } from "hooks";
import { useDidMountEffect } from "hooks/useDidMountEffect";
import React, { useEffect } from "react";
import { OrderDetails } from "../Details/Orders";
import { ParcelDetails } from "../Details/Parcels";
import { ParcelTrackingTable } from "./ParcelOrderTable";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: "#f5f6f8",
      display: "flex",
      flexDirection: "row",
      width: "100%",
      marginBottom: 0,
      paddingTop: 60
    },
    left: {
      backgroundColor: "#fff",
      padding: "24px",
      flexDirection: "column",
      margin: "24px 30px 0 30px",
      borderRadius: "12px",
      height: "90vh",
      [theme.breakpoints.down("xs")]: {
        backgroundColor: "inherit",
        margin: "16px 16px 0 16px",
        height: "90vh",
      },
    },
    details: {
      width: "30%",
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
  })
);

export function ParcelsTrackingList() {
  const { client } = useCurrentClient();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const styles = useStyles();
  const { text } = useText();
  const [switched, setSwitched] = React.useState<boolean>(false);
  const [order, setOrder] = React.useState<Order>(null);
  const [parcel, setParcel] = React.useState<Parcel>(null);
  const [openSlider, setOpenSlider] = React.useState(false);
  const [key, setKey] = React.useState("onprogress");
  const { selectedDate, selectedStatus } = React.useContext(dateContext);
  const [state, dispatch] = React.useReducer(parcelsAndOrdersReducer, {
    orders: {
      successful: [],
      cancelled: [],
      onprogress: [],
    },
    parcels: {
      successful: [],
      cancelled: [],
      onprogress: [],
    },
  });
  const [deleteOrderMutation] = useMutation(deleteOrder);
  const [page, setPage] = React.useState(0);
  const [countTotal, setCountTotal] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [searchText, setSearchText] = React.useState("");
  const prepareQuery = () => {
    const query: any = {};
    const { from, to } = selectedDate;
    query.from_date = from ?? undefined;
    query.to_date = to ?? undefined;
    query.limit = rowsPerPage;
    return query;
  };

  const [updateOrderMutation] = useMutation(UPDATE_ORDER, {
    refetchQueries: [
      {
        query: ReceiverByClientID,
        variables: {
          params: { clientID: client?._id },
        },
      },
    ],
  });
  const dispatchAlert = useAlertDispatch();

  const errorHandled = () => {
    dispatchAlert({
      severity: "error",
      message: text.all.error,
    });
  };
  const onSetRowPerPage = (props: number) => {
    setRowsPerPage(props);
  };

  // Queries

  const [allParcelsByClient, { refetch: refetchParcelData, loading: loadingParcel }] = useLazyQuery(
    GET_PARCELS_BY_CLIENT,
    {
      onCompleted: (data) => {
        setCountTotal(data?.results?.countTotal);
        setCurrentPage(data?.results?.currentPage);
        dispatch({
          type: ParcelActionType.FetchParcels,
          data: data?.results?.parcels,
        });
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const getAllParcels = (page: number) => {
    const query = prepareQuery();
    allParcelsByClient({
      variables: {
        params: {
          clientID: client?._id,
        },
        queries: {
          page: page + 1,
          limit: rowsPerPage,
          status: selectedStatus?.length ? selectedStatus : ParcelStatusList[key],
          search: searchText,
          ...query,
        },
      },
    });
  };
  

  const [allOrdersByClient, { refetch: refetchOrderData, loading: loadingOrder }] = useLazyQuery(GET_ORDERS_BY_CLIENT, {
    onCompleted: (data) => {
      setCountTotal(data?.results?.countTotal);
      dispatch({
        type: OrderActionType.FetchOrders,
        data: data?.results?.orders,
      });
    },
    fetchPolicy: "cache-and-network",
  });
  const getAllOrders = (page: number) => {
    const query = prepareQuery();
    allOrdersByClient({
      variables: {
        params: {
          clientID: client?._id,
        },
        queries: {
          page: page + 1,
          limit: rowsPerPage,
          status: selectedStatus?.length ? selectedStatus : OrderStatusList[key],
          search: searchText,
          ...query,
        },
      },
    });
  };

  useSubscription(AllParcelsSubscription, {
    variables: { client_UID: client?._id },
    onSubscriptionData(options) {
      dispatch({
        type: ParcelActionType.UpdateParcels,
        data: options.subscriptionData.data.parcels,
      });
    },
  });

  useSubscription(AllOrderSubscription, {
    variables: { client_UID: client?._id },
    onSubscriptionData(options) {
      dispatch({
        type: OrderActionType.UpdateOrders,
        data: options.subscriptionData.data.orders,
      });
    },
  });

  const handleSearch = (searchText: string) => {
    if (searchText.includes("pp-o")) {
      const textWithouPP = searchText.replace("pp-", "");
      if (searchText.length === 16) {
        const parcelCode = textWithouPP.substring(textWithouPP.length - 4);
        setSearchText(parcelCode);
      } else {
        setSearchText(textWithouPP);
      }
    } else {
      setSearchText(searchText);
    }
  };

  const getParcelsOrdersData = () => {
    switched ? getAllOrders(page) : getAllParcels(page);
  };

  useEffect(() => {
    if (client) {
      getParcelsOrdersData();
    }
  }, [client, selectedDate, page, selectedStatus, searchText, switched, rowsPerPage]);

  useDidMountEffect(() => {
    setParcel(null);
    setOrder(null);
    setOpenSlider(false);
  }, []);

  const actionSwitcher = (tab: TabProps) => {
    setOpenSlider(false);
    setKey(tab.id);
  };

  const switcher = (value: boolean) => {
    setSwitched(value);
    setOpenSlider(false);
  };

  const onClickOrder = (order: Order) => {
    setOpenSlider(true);
    setOrder(order);
  };

  const onParcelCLick = (parcel: Parcel) => {
    setOpenSlider(true);
    setParcel(parcel);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlerDeleteOrder = () => {
    deleteOrderMutation({
      variables: {
        input: {
          id: order._id,
        },
      },
    })
      .then((response) => {
        const { message, data } = response.data.deleteOrder;
        dispatchAlert({
          message,
          severity: data ? "success" : "error",
        });
        if (data) {
          dispatch({
            type: OrderActionType.RemoveOrders,
            data: [{ ...order }],
            statusKey: key,
          });
          dispatch({
            type: ParcelActionType.RemoveParcels,
            data: order.order_parcels,
          });
          setOrder(null);
        }
      })
      .catch(errorHandled);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlerUpdate = (updated: Order, changed: boolean = false, close: any, orderUpdated: Order) => {
    let input;
    if (changed) {
      input = {
        input: {
          id: orderUpdated._id,
          changeReceiver: {
            ...formatReceiver(updated.order_receiver),
            receiver_address: {
              place_id: updated.order_receiver.receiver_address.place_id,
              additional_address: updated.order_receiver.receiver_address.additional_address,
            },
          },
        },
      };
    } else {
      input = {
        input: {
          id: updated._id,
          receiver: {
            ...formatReceiver(updated.order_receiver),
            receiver_address: {
              place_id: updated.order_receiver.receiver_address.place_id,
              additional_address: updated.order_receiver.receiver_address.additional_address,
            },
          },
        },
      };
    }
    updateOrderMutation({
      variables: {
        ...input,
      },
    })
      .then((response) => {
        const { code, message, data } = response.data.updateOrder;
        if (code === 200) {
          const { order_receiver } = data;
          setOrder((prev) => ({ ...prev, order_receiver }));
          dispatch({
            type: OrderActionType.UpdateOrders,
            data: [{ ...order, order_receiver }],
          });
        }
        dispatchAlert({
          severity: code === 200 ? "success" : "error",
          message,
        });
        if (changed) {
          close(false);
        }
      })
      .catch((error) => {
        dispatchAlert({ message: error.message, severity: "error" });
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handlerAddNewParcel = (parcel: Parcel, orderUpdated: Order) => {
    updateOrderMutation({
      variables: {
        input: {
          id: orderUpdated._id,
          newParcels: [formatParcel(parcel)],
        },
      },
    })
      .then((response) => {
        const { code, message, data } = response.data.updateOrder;
        dispatchAlert({
          severity: code === 200 ? "success" : "error",
          message,
        });
        if (code === 200) {
          const { order_parcels } = data;
          setOrder((prev) => ({ ...prev, order_parcels }));
          dispatch({
            type: OrderActionType.UpdateOrders,
            data: [{ ...order, order_parcels }],
            statusKey: key,
          });
          dispatch({
            type: ParcelActionType.UpdateParcels,
            data: order_parcels.map((p: Parcel) => ({ ...p, parcel_order: data })),
          });
        }
      })
      .catch((error) => {
        dispatchAlert({ message: error.message, severity: "error" });
      });
  };

  const EventsView = React.useCallback(
    () => (switched ? <OrderDetails orderId={order?._id} /> : <ParcelDetails parcel={parcel} />),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [switched, order, parcel]
  );
  const handleChangePage = (event: React.ChangeEvent<{}>, newpage: number) => {
    setPage(newpage);
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.left}
        style={{
          width: isSmallScreen ? "100vw" : "70%",
        }}
      >
        <ParcelTrackingTable
          actionSwitcher={actionSwitcher}
          parcels={{ data: state?.parcels[key], loading: loadingParcel }}
          switcher={switcher}
          ordersData={{
            orders: state?.orders[key],
            orderLoading: loadingOrder,
          }}
          refetchParcels={refetchParcelData}
          refetchOrders={refetchOrderData}
          handleChangePage={handleChangePage}
          handleSearch={handleSearch}
          onClickOrder={onClickOrder}
          onClickParcel={onParcelCLick}
          statusKey={key}
          handlerAddNewParcel={handlerAddNewParcel}
          handlerDeleteOrder={handlerDeleteOrder}
          handlerUpdate={handlerUpdate}
          state={state && state}
          currentPage={page}
          countTotal={countTotal}
          setRowsPerPage={onSetRowPerPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
        />
      </div>
      <Slider openDrawer={openSlider} handleClose={() => setOpenSlider(false)}>
        <div className={styles.details}>
          <EventsView />
        </div>
      </Slider>
    </div>
  );
}
